import React from 'react';
import { Link } from 'react-router-dom';
import { AlertOctagon, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { useStuckInvoices, RecheckStripeButton, fmtUsd, fmtAge } from './stuckInvoices';

/** Red banner on the Owner Admin dashboard: Stripe says paid, invoice still unpaid > 1h. */
export default function StuckInvoiceAlert() {
  const { data, loading, refresh } = useStuckInvoices({ checkStripe: true, limit: 25 });
  const stuck = (data?.items || []).filter((i) => i.stuck);

  if (loading || !stuck.length) return null;

  return (
    <div
      className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 space-y-3"
      role="alert"
      data-testid="stuck-invoice-alert"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <AlertOctagon className="h-5 w-5 text-red-400 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-white" data-testid="stuck-invoice-alert-title">
              {stuck.length} invoice{stuck.length > 1 ? 's' : ''} paid on Stripe but still unpaid in CountBucks
            </p>
            <p className="text-sm text-red-200/80">
              The Stripe webhook was missed or delayed for over an hour. Re-check to record the payment.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={refresh} className="text-red-200" data-testid="stuck-alert-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button asChild variant="outline" size="sm" className="border-red-500/40 text-red-200">
            <Link to="/owner-admin/payments" data-testid="stuck-alert-view-all">
              View all <ExternalLink className="h-3.5 w-3.5 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
      <ul className="divide-y divide-red-500/20">
        {stuck.slice(0, 5).map((i) => (
          <li key={i.invoice_id} className="flex items-center justify-between gap-3 py-2" data-testid={`stuck-alert-row-${i.invoice_id}`}>
            <div className="text-sm min-w-0">
              <span className="text-white font-medium">#{i.invoice_no || i.invoice_id}</span>
              <span className="text-red-200/70"> · {i.workspace_name || i.workspace_id} · {i.customer_name || 'Customer'} · {fmtUsd(i.total)}</span>
              <span className="text-red-200/50"> · checkout {fmtAge(i.age_minutes)} ago</span>
            </div>
            <RecheckStripeButton invoiceId={i.invoice_id} onDone={refresh} />
          </li>
        ))}
      </ul>
    </div>
  );
}
